import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, ChevronLeft, ChevronRight, Car } from "lucide-react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api, VENMO_QR, VENMO_HANDLE, VENMO_URL, PHONE } from "../lib/api";
import { PACKAGES, ADDONS, PACKAGE_MINUTES, WEEKDAY_MAX_MIN } from "../data/content";
import UpgradeModal from "./booking/UpgradeModal";
import AddonsFeesStep from "./booking/AddonsFeesStep";
import PaymentStep from "./booking/PaymentStep";

const SIZES = [{ k: "sedan", label: "Sedan" }, { k: "suv", label: "3-Row SUV" }, { k: "truck", label: "Truck" }];
const SIG_MAP = { premium_interior: "signature_interior", premium_full: "signature_full", wash_wax: "wash_clay_coat" };
const FREE_LABEL = "Exhaust Tip + Gas Cap Clean (FREE)";
const priceFor = (pkg, size) => (size === "sedan" ? pkg.sedan : pkg.suv);

export default function BookNow() {
  const [step, setStep] = useState(0);
  const [carCount, setCarCount] = useState(1);
  const [cars, setCars] = useState([{ size: "sedan", package: "", lifted: false }]);
  const [addonSel, setAddonSel] = useState({}); // key -> tier index
  const [freeAddons, setFreeAddons] = useState([]); // free labels from coupon/upsell
  const [fees, setFees] = useState({ address: "", outside_windward: false, disaster: false });
  const [distance, setDistance] = useState(null);
  const [checking, setChecking] = useState(false);
  const [info, setInfo] = useState({ full_name: "", phone: "", email: "", water_access: true, power_access: true, agree: false });
  const [dateData, setDateData] = useState([]);
  const [selDate, setSelDate] = useState("");
  const [selSlot, setSelSlot] = useState("");
  const [payType, setPayType] = useState("deposit");
  const [payMethod, setPayMethod] = useState("stripe");
  const [maintPlan, setMaintPlan] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [codeInfo, setCodeInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeDecided, setUpgradeDecided] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [giftCode, setGiftCode] = useState("");
  const [giftInfo, setGiftInfo] = useState(null);
  const [nextCoupon, setNextCoupon] = useState("");

  useEffect(() => {
    const start = new Date().toISOString().slice(0, 10);
    api.get(`/availability?start=${start}&days=35`)
      .then((r) => setDateData(r.data.days))
      .catch(() => toast.error("Couldn't load time slots — please retry in a moment."));
  }, []);

  useEffect(() => {
    setCars((prev) => {
      const next = [...prev];
      while (next.length < carCount) next.push({ size: "sedan", package: "", lifted: false });
      return next.slice(0, carCount);
    });
  }, [carCount]);

  const hasTruck = cars.some((c) => c.size === "truck" || c.size === "suv");
  const addonSize = hasTruck ? "suv" : "sedan";

  const { addonSubtotal, addonMinutes } = useMemo(() => {
    let s = 0, m = 0;
    Object.entries(addonSel).forEach(([key, tier]) => {
      const a = ADDONS.find((x) => x.key === key);
      if (!a) return;
      const t = a.tiers[Math.min(tier, a.tiers.length - 1)];
      s += t[addonSize]; m += t.min;
    });
    return { addonSubtotal: s, addonMinutes: m };
  }, [addonSel, addonSize]);

  const packageMin = useMemo(() => cars.reduce((m, c) => m + (PACKAGE_MINUTES[c.package] || 0), 0), [cars]);
  const jobMinutes = packageMin + addonMinutes;

  const total = useMemo(() => {
    let t = 0;
    cars.forEach((c) => {
      const pkg = PACKAGES.find((p) => p.key === c.package);
      if (!pkg) return;
      t += priceFor(pkg, c.size === "sedan" ? "sedan" : "suv");
      if (c.lifted) t += pkg.group === "In & Out" ? 50 : 25;
    });
    if (fees.outside_windward) t += 20;
    if (fees.disaster) t += 100;
    t += addonSubtotal;
    return t;
  }, [cars, fees, addonSubtotal]);

  const MAINT_PCT = { quarterly: 10, monthly: 20, biweekly: 33, weekly: 50 };
  const maintPct = MAINT_PCT[maintPlan] || 0;
  const codePct = codeInfo?.percent || 0;
  const codeScope = codeInfo?.applies_to || "total";
  const maintAmt = total * maintPct / 100;
  const codeBase = codeScope === "addons" ? addonSubtotal : total;
  const codeAmt = codeBase * codePct / 100;
  const discountAmt = Math.min(maintAmt + codeAmt, total * 0.6);
  const discountedTotal = Math.round((total - discountAmt) * 100) / 100;
  const discountPct = total > 0 ? Math.round((discountAmt / total) * 100) : 0;
  const deposit = payType === "deposit" ? Math.round(discountedTotal * 0.3 * 100) / 100 : discountedTotal;
  const giftApplied = giftInfo ? Math.min(giftInfo.balance, deposit) : 0;
  const amountDue = Math.round((deposit - giftApplied) * 100) / 100;

  const updateCar = (i, patch) => setCars((p) => p.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const toggleAddon = (key) => setAddonSel((s) => { const n = { ...s }; if (key in n) delete n[key]; else n[key] = 0; return n; });
  const setTier = (key, ti) => setAddonSel((s) => ({ ...s, [key]: ti }));

  const checkAddress = async () => {
    if (!fees.address.trim()) { toast.error("Enter an address first."); return; }
    setChecking(true);
    try {
      const { data } = await api.post("/quote-distance", { address: fees.address });
      setDistance(data);
      setFees((f) => ({ ...f, outside_windward: data.fee > 0 }));
      if (data.come_to_shop) toast.warning("Over 40 min away — a shop visit is required.");
      else toast.success(data.message);
    } catch { toast.error("Could not check that address."); }
    setChecking(false);
  };

  const applyCode = async () => {
    if (!discountCode.trim()) return;
    try {
      const { data } = await api.get(`/validate-discount?code=${encodeURIComponent(discountCode.trim())}`);
      if (data.valid) {
        setCodeInfo(data);
        if (data.reward && /exhaust/i.test(data.reward)) setFreeAddons((f) => (f.includes(FREE_LABEL) ? f : [...f, FREE_LABEL]));
        toast.success(data.type === "coupon" ? `Coupon applied — ${data.reward || "reward unlocked"}!` : `${data.name} applied — ${data.percent}% off!`);
      } else { setCodeInfo(null); toast.error("That code isn't active."); }
    } catch { toast.error("Could not validate code."); }
  };

  const applyGift = async () => {
    if (!giftCode.trim()) return;
    try {
      const { data } = await api.get(`/giftcards/validate?code=${encodeURIComponent(giftCode.trim())}`);
      if (data.valid) { setGiftInfo(data); toast.success(`Gift card applied — $${data.balance.toFixed(2)} available!`); }
      else { setGiftInfo(null); toast.error("That gift card isn't valid or has no balance."); }
    } catch { toast.error("Could not validate gift card."); }
  };

  const steps = ["Vehicles", "Packages", "Add-ons & Fees", "Details", "Schedule", "Payment"];

  const canNext = () => {
    if (step === 1) return cars.every((c) => c.package);
    if (step === 2) return !!fees.address.trim() && !!distance;
    if (step === 3) return info.full_name.trim() && info.phone.replace(/\D/g, "").length === 10 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(info.email) && info.agree;
    if (step === 4) return selDate && selSlot;
    return true;
  };

  const claimUpgrade = async () => {
    setCars((cs) => cs.map((c) => (SIG_MAP[c.package] ? { ...c, package: SIG_MAP[c.package] } : c)));
    setFreeAddons((a) => (a.includes(FREE_LABEL) ? a : [...a, FREE_LABEL]));
    setUpgradeDecided(true);
    setCouponCode("PINKUP-GIFT");
    try { const { data } = await api.post("/claim-coupon", { kind: "signature_upgrade" }); if (data?.code) setCouponCode(data.code); } catch (e) { if (process.env.NODE_ENV === "development") console.error("Coupon claim failed:", e); }
  };
  const copyCoupon = async () => {
    try { await navigator.clipboard.writeText(couponCode); setCopied(true); toast.success("Coupon copied!"); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Copy failed — select and copy manually."); }
  };
  const handleNext = () => {
    if (!canNext()) { toast.error("Please complete this step."); return; }
    if (step === 1 && !upgradeDecided && carCount === 1 && cars[0]?.package === "premium_full") { setShowUpgrade(true); return; }
    setStep((s) => s + 1);
  };

  const submit = async () => {
    setLoading(true);
    try {
      const payload = {
        cars: cars.map((c) => ({ size: c.size, package: c.package, lifted: c.lifted })),
        addon_items: Object.entries(addonSel).map(([key, tier]) => ({ key, tier })),
        addons: freeAddons,
        address: fees.address, outside_windward: fees.outside_windward, disaster: fees.disaster,
        full_name: info.full_name, phone: info.phone, email: info.email, water_access: info.water_access, power_access: info.power_access,
        date: selDate, time_slot: selSlot, payment_type: payType, payment_method: payMethod,
        maintenance_plan: maintPlan, discount_code: codeInfo ? discountCode.trim() : "",
        address_resolved: distance ? !!distance.resolved : false,
        gift_code: giftInfo ? giftCode.trim() : "",
      };
      const { data: booking } = await api.post("/bookings", payload);
      window._venmoBooking = booking;
      setNextCoupon(booking.next_coupon_code || "");
      if (booking.amount_due <= 0) {
        toast.success("Booking confirmed — your gift card covered it!");
        setStep(7); setLoading(false); return;
      }
      if (payMethod === "venmo") {
        toast.success("Booking created! Complete your Venmo payment to confirm.");
        setStep(6); setLoading(false); return;
      }
      const { data } = await api.post("/payments/checkout", { booking_id: booking.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create booking.");
      setLoading(false);
    }
  };

  return (
    <Layout>
      <section className="max-w-3xl mx-auto px-6 py-12 md:py-16">
        {showUpgrade && (
          <UpgradeModal
            couponCode={couponCode}
            copied={copied}
            onClaim={claimUpgrade}
            onCopy={copyCoupon}
            onDecline={() => { setUpgradeDecided(true); setShowUpgrade(false); setStep((s) => s + 1); }}
            onContinue={() => { setShowUpgrade(false); setStep((s) => s + 1); }}
          />
        )}
        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
          {steps.map((s, i) => (
            <div key={s} className="flex items-center gap-2 shrink-0">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i < step ? "bg-pink-600" : i === step ? "bg-pink-600 ring-4 ring-pink-600/30" : "bg-white/10 text-zinc-500"}`}>{i < step ? <Check size={14} /> : i + 1}</div>
              <span className={`text-xs ${i === step ? "text-white" : "text-zinc-500"}`}>{s}</span>
              {i < steps.length - 1 && <div className="w-4 h-px bg-white/15" />}
            </div>
          ))}
        </div>

        <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
          {step === 0 && (
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight">How many cars?</h1>
              <p className="text-zinc-400 mt-2">Select up to 3 vehicles for this booking.</p>
              <div className="grid grid-cols-3 gap-4 mt-6">
                {[1, 2, 3].map((n) => (
                  <button key={n} onClick={() => setCarCount(n)} className={`rounded-2xl border p-8 transition-all ${carCount === n ? "border-pink-500 bg-[#1a1013]" : "border-white/10 bg-[#121212] hover:border-white/25"}`} data-testid={`car-count-${n}`}>
                    <Car className="mx-auto text-pink-500" size={28} /><p className="text-2xl font-extrabold mt-3">{n}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight">Choose a package per car</h1>
              {cars.map((c, i) => (
                <div key={i} className="mt-6 rounded-2xl border border-white/10 bg-[#121212] p-5">
                  <p className="font-bold mb-3">Car {i + 1}</p>
                  <div className="flex gap-2 mb-4">
                    {SIZES.map((s) => (<button key={s.k} onClick={() => updateCar(i, { size: s.k })} className={`rounded-full px-4 py-2 text-sm ${c.size === s.k ? "bg-pink-600" : "bg-white/5 border border-white/15"}`} data-testid={`car-${i}-size-${s.k}`}>{s.label}</button>))}
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {PACKAGES.map((p) => (
                      <button key={p.key} onClick={() => updateCar(i, { package: p.key })} className={`text-left rounded-xl border p-3 transition-all ${c.package === p.key ? "border-pink-500 bg-[#1a1013]" : "border-white/10 hover:border-white/25"}`} data-testid={`car-${i}-pkg-${p.key}`}>
                        <p className="text-sm font-semibold">{p.name}</p>
                        <p className="text-xs text-zinc-500">{p.group} · ${priceFor(p, c.size === "sedan" ? "sedan" : "suv")}</p>
                      </button>
                    ))}
                  </div>
                  {(c.size === "suv" || c.size === "truck") && (
                    <label className="flex items-center gap-2 mt-3 text-sm text-zinc-300 cursor-pointer">
                      <input type="checkbox" checked={c.lifted} onChange={(e) => updateCar(i, { lifted: e.target.checked })} data-testid={`car-${i}-lifted`} /> Lifted / large truck (adds fee)
                    </label>
                  )}
                </div>
              ))}
            </div>
          )}

          {step === 2 && (
            <AddonsFeesStep
              fees={fees} setFees={setFees} setDistance={setDistance}
              distance={distance} checking={checking} checkAddress={checkAddress}
              addonSize={addonSize} jobMinutes={jobMinutes}
              addonSel={addonSel} toggleAddon={toggleAddon} setTier={setTier}
              addonSubtotal={addonSubtotal} freeAddons={freeAddons}
            />
          )}

          {step === 3 && (
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight">Your details</h1>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <input value={info.full_name} onChange={(e) => setInfo({ ...info, full_name: e.target.value })} placeholder="Full name *" className="bg-[#050505] border border-white/20 focus:border-pink-500 rounded-lg px-4 py-3 outline-none" data-testid="book-name" />
                <input value={info.phone} onChange={(e) => setInfo({ ...info, phone: e.target.value })} placeholder="Phone number (10 digits) *" inputMode="tel" className="bg-[#050505] border border-white/20 focus:border-pink-500 rounded-lg px-4 py-3 outline-none" data-testid="book-phone" />
                <input value={info.email} onChange={(e) => setInfo({ ...info, email: e.target.value })} placeholder="Email address *" type="email" autoCapitalize="none" autoCorrect="off" className="sm:col-span-2 bg-[#050505] border border-white/20 focus:border-pink-500 rounded-lg px-4 py-3 outline-none" data-testid="book-email" />
              </div>
              {info.phone && info.phone.replace(/\D/g, "").length !== 10 && <p className="mt-2 text-xs text-amber-400" data-testid="phone-hint">Phone must be exactly 10 digits.</p>}
              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                <label className="flex items-center gap-2 rounded-xl border border-white/10 bg-[#121212] p-4 cursor-pointer"><input type="checkbox" checked={info.water_access} onChange={(e) => setInfo({ ...info, water_access: e.target.checked })} data-testid="book-water" /> I have access to water</label>
                <label className="flex items-center gap-2 rounded-xl border border-white/10 bg-[#121212] p-4 cursor-pointer"><input type="checkbox" checked={info.power_access} onChange={(e) => setInfo({ ...info, power_access: e.target.checked })} data-testid="book-power" /> I have access to power</label>
              </div>
              <label className="flex items-start gap-3 mt-4 text-sm text-zinc-300 cursor-pointer">
                <input type="checkbox" checked={info.agree} onChange={(e) => setInfo({ ...info, agree: e.target.checked })} className="mt-1" data-testid="book-agree" />
                <span>I agree to the <Link to="/terms" target="_blank" className="text-pink-500 underline">liability terms & service</Link>, including the deposit and cancellation policy.</span>
              </label>
            </div>
          )}

          {step === 4 && (
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight">Pick a day & time</h1>
              <p className="text-zinc-400 mt-2">Weekdays: one evening slot (max {WEEKDAY_MAX_MIN} min job). Weekends: 9AM–5:30PM. One booking per day; the crew keeps one weekend day free.</p>
              {jobMinutes > WEEKDAY_MAX_MIN && (
                <p className="mt-3 text-xs text-amber-400" data-testid="weekday-time-hint">This job is about {jobMinutes} min — too long for a weekday evening, so weekdays are disabled. Please choose a weekend day.</p>
              )}
              <div className="mt-5 grid grid-cols-4 sm:grid-cols-7 gap-2 max-h-64 overflow-y-auto">
                {dateData.map((d) => {
                  const dt = new Date(d.date + "T00:00:00");
                  const wd = dt.getDay();
                  const tooLong = wd >= 1 && wd <= 5 && jobMinutes > WEEKDAY_MAX_MIN;
                  const disabled = d.blocked || tooLong;
                  return (
                    <button key={d.date} disabled={disabled} onClick={() => { setSelDate(d.date); setSelSlot(""); }} className={`rounded-xl border p-2 text-center transition-all ${disabled ? "opacity-25 cursor-not-allowed border-white/10" : selDate === d.date ? "border-pink-500 bg-[#1a1013]" : "border-white/10 hover:border-white/30"}`} data-testid={`date-${d.date}`}>
                      <p className="text-[10px] text-zinc-500">{dt.toLocaleDateString("en-US", { weekday: "short" })}</p>
                      <p className="text-sm font-bold">{dt.getDate()}</p>
                    </button>
                  );
                })}
              </div>
              {selDate && (
                <div className="mt-5">
                  <p className="text-sm font-semibold mb-2">Available times</p>
                  <div className="flex flex-wrap gap-2">
                    {(dateData.find((d) => d.date === selDate)?.slots || []).map((s) => (<button key={s} onClick={() => setSelSlot(s)} className={`rounded-full px-4 py-2 text-sm ${selSlot === s ? "bg-pink-600" : "bg-white/5 border border-white/15"}`} data-testid={`slot-${s}`}>{s}</button>))}
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 5 && (
            <PaymentStep
              total={total} addonSubtotal={addonSubtotal}
              maintPlan={maintPlan} setMaintPlan={setMaintPlan}
              discountCode={discountCode} setDiscountCode={setDiscountCode} setCodeInfo={setCodeInfo}
              applyCode={applyCode} codeInfo={codeInfo}
              giftCode={giftCode} setGiftCode={setGiftCode} setGiftInfo={setGiftInfo}
              applyGift={applyGift} giftInfo={giftInfo}
              discountAmt={discountAmt} codeScope={codeScope} discountPct={discountPct}
              payType={payType} setPayType={setPayType} discountedTotal={discountedTotal}
              giftApplied={giftApplied} amountDue={amountDue}
              payMethod={payMethod} setPayMethod={setPayMethod}
            />
          )}

          {step === 6 && (
            <div className="text-center">
              <h1 className="text-3xl font-extrabold tracking-tight">Complete your Venmo payment</h1>
              <p className="text-zinc-400 mt-2">Send <span className="text-pink-500 font-bold">${amountDue.toFixed(2)}</span> to confirm your booking.</p>
              <img src={VENMO_QR} alt="Venmo QR" className="mx-auto mt-6 w-56 rounded-xl border border-white/10" />
              <p className="mt-4 text-lg font-bold">{VENMO_HANDLE}</p>
              <a href={VENMO_URL} target="_blank" rel="noopener noreferrer" className="text-pink-500 underline text-sm">Open in Venmo</a>
              {nextCoupon && <p className="mt-4 text-sm text-green-400" data-testid="next-coupon-venmo">Bonus: <span className="font-mono font-bold">{nextCoupon}</span> — 5% off your next detail (also emailed).</p>}
              <p className="text-sm text-zinc-500 mt-4">After paying, text us at {PHONE} to finalize. We'll confirm your slot.</p>
              <Link to="/" className="mt-8 inline-flex bg-pink-600 hover:bg-pink-500 rounded-full px-8 py-3 font-semibold transition-colors">Done</Link>
            </div>
          )}

          {step === 7 && (
            <div className="text-center" data-testid="booking-confirmed">
              <div className="mx-auto w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center"><Check className="text-green-500" size={32} /></div>
              <h1 className="text-3xl font-extrabold tracking-tight mt-6">Booking confirmed! 🎉</h1>
              <p className="text-zinc-400 mt-3 max-w-md mx-auto">You're all set. We've emailed your confirmation — see you {selDate} at {selSlot}!</p>
              {nextCoupon && <p className="mt-4 text-sm text-green-400" data-testid="next-coupon-confirmed">Bonus coupon: <span className="font-mono font-bold">{nextCoupon}</span> — 5% off your next detail.</p>}
              <Link to="/" className="mt-8 inline-flex bg-pink-600 hover:bg-pink-500 rounded-full px-8 py-3 font-semibold transition-colors">Back home</Link>
            </div>
          )}
        </motion.div>

        {step < 6 && (
          <div className="mt-10 flex items-center justify-between">
            <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} className="flex items-center gap-1 text-zinc-400 hover:text-white disabled:opacity-30" data-testid="book-back"><ChevronLeft size={18} /> Back</button>
            {step < 5 ? (
              <button onClick={handleNext} className="flex items-center gap-1 bg-pink-600 hover:bg-pink-500 rounded-full px-7 py-3 font-semibold transition-colors active:scale-95" data-testid="book-next">Next <ChevronRight size={18} /></button>
            ) : (
              <button onClick={submit} disabled={loading} className="bg-pink-600 hover:bg-pink-500 rounded-full px-7 py-3 font-semibold transition-colors active:scale-95 disabled:opacity-50" data-testid="book-pay">{loading ? "Processing…" : payMethod === "stripe" ? `Pay $${amountDue.toFixed(2)}` : "Confirm & show Venmo"}</button>
            )}
          </div>
        )}
      </section>
    </Layout>
  );
}
